import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Mail, Phone, MapPin, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import EditableText from '@/components/EditableText';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY = { name: '', email: '', phone: '', subject: '', message: '' };

export default function ContactPage() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    axios.get(`${API}/settings/public`).then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const contactEmail = settings?.contact_email || 'contact@cresa-asbl.org';
  const contactPhone = settings?.contact_phone || '+243 853 717 311';
  const contactPhone2 = settings?.contact_phone_2 || '+243 847 143 388';
  const contactAddress = settings?.contact_address || 'Avenue des Martyrs ex-VAMARO N°05, Bukavu, RD Congo';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success('Message envoyé. Nous vous répondrons rapidement.');
      setForm(EMPTY);
    } catch (err) {
      console.error('Contact send failed:', err);
      // Fallback: mailto
      const subject = encodeURIComponent(`[CRESA Contact] ${form.subject || 'Demande de contact'}`);
      const body = encodeURIComponent(
        `${form.message}\n\n— ${form.name}\n${form.email}${form.phone ? `\n${form.phone}` : ''}`
      );
      window.location.href = `mailto:contact@cresa-asbl.org?subject=${subject}&body=${body}`;
      toast.info('Ouverture de votre messagerie…');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />
      <section className="section-padding bg-[#F9F8F6]" data-testid="contact-page">
        <div className="container mx-auto px-6">
          <div className="text-center mb-10">
            <h1 className="text-4xl sm:text-5xl font-bold text-[#2E332D] mb-3"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Contactez-nous
            </h1>
            <p className="text-base text-[#5C665A] max-w-2xl mx-auto">
              <EditableText contentKey="contact.intro">
                Une question, un partenariat, une opportunité d&apos;appui ? Écrivez-nous.
              </EditableText>
            </p>
          </div>

          <div className="grid lg:grid-cols-5 gap-8 max-w-6xl mx-auto">
            <aside className="lg:col-span-2 space-y-4">
              <div className="card" data-testid="contact-info-block">
                <h2 className="text-xl font-bold text-[#2E332D] mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  Coordonnées
                </h2>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <MapPin className="text-[#4A6B48] mt-1 shrink-0" size={20} />
                    <div>
                      <p className="text-sm font-bold text-[#2E332D]">Siège social</p>
                      <p className="text-sm text-[#5C665A]">{contactAddress}</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Mail className="text-[#4A6B48] mt-1 shrink-0" size={20} />
                    <div>
                      <p className="text-sm font-bold text-[#2E332D]">Email</p>
                      <a href={`mailto:${contactEmail}`} className="text-sm text-[#4A6B48] hover:underline">{contactEmail}</a>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Phone className="text-[#4A6B48] mt-1 shrink-0" size={20} />
                    <div>
                      <p className="text-sm font-bold text-[#2E332D]">Téléphone</p>
                      <a href={`tel:${contactPhone.replace(/\s/g, '')}`} className="text-sm text-[#5C665A] hover:text-[#4A6B48] block">{contactPhone}</a>
                      {contactPhone2 && (
                        <a href={`tel:${contactPhone2.replace(/\s/g, '')}`} className="text-sm text-[#5C665A] hover:text-[#4A6B48] block">{contactPhone2}</a>
                      )}
                    </div>
                  </li>
                </ul>
              </div>
              <div className="card bg-[#4A6B48] text-white">
                <p className="text-sm leading-relaxed">
                  CRESA est enregistrée sous : <span className="font-bold">JUST.112/S-KV/3917/2013</span>
                </p>
                <p className="text-xs italic text-white/80 mt-2">
                  Organisation de droit congolais
                </p>
              </div>
            </aside>

            <form onSubmit={handleSubmit} className="card lg:col-span-3 space-y-4" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-[#2E332D] mb-1">Nom complet *</label>
                  <input
                    type="text" required value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg"
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-[#2E332D] mb-1">Email *</label>
                  <input
                    type="email" required value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg"
                    data-testid="contact-email-input"
                  />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-[#2E332D] mb-1">Téléphone</label>
                  <input
                    type="tel" value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg"
                    data-testid="contact-phone-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-[#2E332D] mb-1">Sujet</label>
                  <input
                    type="text" value={form.subject}
                    onChange={(e) => setForm({ ...form, subject: e.target.value })}
                    placeholder="Partenariat, question, appui…"
                    className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg"
                    data-testid="contact-subject-input"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-bold text-[#2E332D] mb-1">Message *</label>
                <textarea
                  required rows={6} value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg"
                  data-testid="contact-message-input"
                />
              </div>
              <button type="submit" disabled={sending}
                data-testid="contact-submit-button"
                className="btn-primary inline-flex items-center justify-center gap-2 w-full sm:w-auto disabled:opacity-50"
              >
                {sending ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />}
                {sending ? 'Envoi en cours…' : 'Envoyer le message'}
              </button>
            </form>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
